import { useEffect, useState } from 'react';
import { Check, Download, Eye, Reply, Sparkles, Webhook } from 'lucide-react';
import { CtaLink, Headline, Kicker, Reveal, Section, Spec, Tile } from '../ui';
import { cn } from '@/lib/utils';
import { JourneyStory } from '../ScrollStory';
import { PipelinePreview } from '../demos/BrandPreviews';

const REPLY = `Hi Jordan — great meeting you at the Heights market on Saturday. You mentioned wanting a set of the Harbor prints for the office; I can do 12×16 framed in oak at $95 each, or we can look at a larger run. Want me to hold two for you?`;

export function LeadsPage() {
  const [typed, setTyped] = useState(0);

  useEffect(() => {
    let i = 0;
    const t = setInterval(() => {
      i += 3;
      setTyped(Math.min(REPLY.length, i));
      if (i >= REPLY.length + 60) i = 0;
    }, 40);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <Section className="play-subhero play-subhero--leads pb-20 pt-16">
        <Reveal className="max-w-3xl">
          <Kicker>Leads · a people-shaped pipeline</Kicker>
          <h1 className="play-subhero__title mt-4">
            Every exchange becomes a lead. <em>Not a pile.</em>
          </h1>
          <p className="play-subhero__body mt-6 max-w-xl">
            When someone taps Exchange, their name, email, phone, note and photo land in a pipeline
            you can actually work: drag it through stages, see when they come back to your card,
            reply from the inbox, export to CSV, or push it to your CRM.
          </p>
        </Reveal>

        <div className="mt-12"><PipelinePreview /></div>
      </Section>

      <Section className="play-page-section py-24">
        <div className="grid gap-4 md:grid-cols-3">
          <Reveal>
            <Tile kicker="Hot leads" title="They came back. You'd know." body="Haphe remembers the visitor who exchanged with you. If they return to your card, tap links, or open it three times in a week, the lead lights up as hot.">
              <div className="flex items-center gap-3 text-xs text-ink-muted"><Eye className="h-4 w-4 text-accent" /> 4 return views · 6 link taps since capture</div>
            </Tile>
          </Reveal>
          <Reveal delay={0.05}>
            <Tile kicker="Stale reminders" title="Three days, no reply, a nudge." body="Leads you haven't answered in three days surface at the top with a clock. Nothing gets lost under the next event's stack.">
              <div className="rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-800">2 leads need a follow-up</div>
            </Tile>
          </Reveal>
          <Reveal delay={0.1}>
            <Tile kicker="Enrichment" title="A company from an email." body="A non-generic email domain becomes a company name and favicon on the card, so you remember which Jordan this is.">
              <div className="flex items-center gap-2 text-xs text-ink-muted"><span className="h-3 w-3 rounded-sm bg-accent" /> Northwind · northwind.co</div>
            </Tile>
          </Reveal>
        </div>
      </Section>

      {/* AI reply */}
      <JourneyStory kind="leads" />
      <Section dark className="play-page-dark play-page-dark--blue py-24">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <Kicker gold>Reply from the inbox</Kicker>
            <Headline className="mt-4 text-bone">The first reply, drafted. You send.</Headline>
            <p className="mt-5 max-w-md text-base leading-relaxed text-bone/65">
              Open a lead, tap Draft, and Haphe writes a first reply from the lead's note, your card,
              and your tone. Edit it or don't. It sends from your inbox with your address as the
              reply-to, so the thread continues in your mail.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-bone/75">
              {['Send from the lead drawer, no copy-paste', 'Replies stamp the lead as answered', 'Drafts never go out without your tap'].map((t) => (
                <li key={t} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 mk-gold" />{t}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-bone/10 bg-white/[0.03] p-5">
              <div className="flex items-center justify-between">
                <span className="mk-kicker text-bone/50">Reply to Jordan Lee</span>
                <span className="flex items-center gap-1 mk-kicker mk-gold"><Sparkles className="h-3 w-3" /> draft</span>
              </div>
              <p className={cn('mt-4 min-h-[140px] text-sm leading-relaxed text-bone/85', typed < REPLY.length && 'mk-caret')}>
                {REPLY.slice(0, typed)}
              </p>
              <div className="mt-4 flex items-center gap-2">
                <span className="flex h-9 items-center gap-1.5 rounded-full bg-bone px-4 text-xs font-medium text-ink"><Reply className="h-3.5 w-3.5" /> Send</span>
                <span className="text-[11px] text-bone/40">from you@yourdomain.com</span>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="play-page-section play-page-section--warm py-24">
        <Reveal className="max-w-2xl">
          <Kicker>Out of Haphe</Kicker>
          <Headline className="mt-4">Your leads are yours.</Headline>
        </Reveal>
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          <Reveal>
            <Spec title="CSV export" rows={[['columns', 'captured, card, status, tag, name, email, phone, company, message, notes'], ['scope', 'all leads, filtered or not'], ['limit', 'none']]} />
          </Reveal>
          <Reveal delay={0.05}>
            <Spec title="Webhook · lead.created" rows={[['destination', 'Zapier, Make, n8n, HubSpot, anything'], ['payload', '{ event, lead, card }'], ['signature', 'HMAC-SHA256, per-card secret'], ['test', 'one tap from the editor']]} />
          </Reveal>
        </div>
        <Reveal className="mt-8 flex flex-wrap gap-3">
          <span className="flex items-center gap-2 text-sm text-ink-muted"><Download className="h-4 w-4" /> Filter by card and tag, then export.</span>
          <span className="flex items-center gap-2 text-sm text-ink-muted"><Webhook className="h-4 w-4" /> Every new lead POSTs within seconds.</span>
        </Reveal>
      </Section>

      <Section dark className="play-page-dark play-page-dark--orange py-24 text-center">
        <Reveal>
          <Headline size="xl" className="mx-auto max-w-3xl text-bone">Meet people. Keep them.</Headline>
          <div className="mt-8 flex justify-center gap-3">
            <CtaLink to="/signup" variant="gold">Get your card, free</CtaLink>
            <CtaLink to="/features/grow" variant="ghost" dark>Next: grow</CtaLink>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
